import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Stack, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, Typography, Divider, Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { HomeContext } from '../utils/contexts/homeContext';
import { ServiceFormContext } from '../utils/contexts/serviceFormContext';
import {
    GetDayJSValueFromUTCDate, GetDateFromDayJSValue, PatientRenewServiceAPIUrl, GetNoToCurrencyFormat, GetNumberFromCurrencyFormat
} from '../utils/extensions/functionEx';
import { DATE_DISPLAY_FORMAT } from '../startups/apiConfig';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const RenewServiceForm = (props) => {
    const { selectedPatientService, handleCloseClick } = props;
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { serviceTypes } = useContext(ServiceFormContext);

    const [cleared, setCleared] = useState(false);
    const [notes, setNotes] = React.useState(selectedPatientService.service_remarks);
    const [serviceCompletedDate, setServiceCompletedDate] = useState(dayjs());
    const [serviceCompletedDutyDays, setServiceCompletedDutyDays] = useState(0);
    const [nurseCompletedDutyDays, setNurseCompletedDutyDays] = useState(0);
    const [serviceExtendedDutyDays, setServiceExtendedDutyDays] = useState(0);
    const [serviceExtendedDutyDaysFee, setServiceExtendedDutyDaysFee] = useState(0);
    const [serviceExtDutyNurseSalary, setServiceExtDutyNurseSalary] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [selectedServiceType, setSelectedServiceType] = useState(null);
    const [renewStartDate, setRenewStartDate] = useState(dayjs());
    const [dutyDays, setDutyDays] = useState(0);
    const [endDate, setEndDate] = useState('');
    const [totalFee, setTotalFee] = useState('0.00');
    const [nurseSalary, setNurseSalary] = useState('0.00');
    const [remarks, setRemarks] = useState('');

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (serviceTypes && selectedPatientService) {
            const tempServiceTypes = serviceTypes.filter(col => col.id === selectedPatientService.service_type_id);
            if (tempServiceTypes.length > 0) {
                setSelectedServiceType({ ...tempServiceTypes[0] });
            }
        }
    }, [serviceTypes, selectedPatientService]);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    useEffect(() => {
        if (renewStartDate && dutyDays) {
            const tempEndDate = renewStartDate.add(parseInt(dutyDays) - 1, 'day')
            setEndDate(tempEndDate.format(DATE_DISPLAY_FORMAT));

            return;
        }
        setEndDate('');
    }, [renewStartDate, dutyDays]);

    useEffect(() => {
        let tempCompletedDutyDays = 0;
        let tempNurseCompletedDutyDays = 0;
        let tempServiceCompletedDate = null;

        if (serviceCompletedDate) {
            tempServiceCompletedDate = dayjs(serviceCompletedDate.year().toString() + '-' + (serviceCompletedDate.month() + 1).toString() + '-' +
                serviceCompletedDate.date().toString());
        }

        if (selectedPatientService.service_start_date && tempServiceCompletedDate) {
            const tempStartDate = GetDayJSValueFromUTCDate(selectedPatientService.service_start_date);
            const date1 = dayjs(tempStartDate.year().toString() + '-' + (tempStartDate.month() + 1).toString() + '-' + tempStartDate.date().toString());

            tempCompletedDutyDays = tempServiceCompletedDate.diff(date1, 'day') + 1;
        }

        if (selectedPatientService.nurse_start_date && tempServiceCompletedDate) {
            const tempNurseStartDate = GetDayJSValueFromUTCDate(selectedPatientService.nurse_start_date);
            const tempNurseStartDateOnly = dayjs(tempNurseStartDate.year().toString() + '-' + (tempNurseStartDate.month() + 1).toString() + '-' + tempNurseStartDate.date().toString());

            tempNurseCompletedDutyDays = tempServiceCompletedDate.diff(tempNurseStartDateOnly, 'day') + 1;
        }

        if (selectedPatientService.service_duty_days) {
            const tempExDutyDays = tempCompletedDutyDays - selectedPatientService.service_duty_days;
            setServiceExtendedDutyDays(tempExDutyDays >= 0 ? tempExDutyDays : 0);
        }
        setServiceCompletedDutyDays(tempCompletedDutyDays);
        setNurseCompletedDutyDays(tempNurseCompletedDutyDays);
    }, [serviceCompletedDate]);

    const handleClose = () => {
        handleCloseClick(false);
    };

    const handleSaveClick = async () => {
        try {
            if (!selectedPatientService || !serviceCompletedDate) {
                OpenErrorAlert('Enter valid completed date.')
                return;
            }
            if (!serviceCompletedDutyDays || serviceCompletedDutyDays.toString().trim().length === 0 || serviceCompletedDutyDays <= 0) {
                OpenErrorAlert('Enter valid completed date.')
                return;
            }
            if (!selectedServiceType) {
                OpenErrorAlert('Enter valid service type.')
                return;
            }
            if (!renewStartDate) {
                OpenErrorAlert('Enter valid renew start date.')
                return;
            }
            if (!dutyDays || parseInt(dutyDays) <= 0) {
                OpenErrorAlert('Enter valid renew duty days.')
                return;
            }
            if (!totalFee || parseInt(GetNumberFromCurrencyFormat(totalFee)) <= 0) {
                OpenErrorAlert('Enter valid renew total fee.')
                return;
            }
            if (!nurseSalary || parseInt(GetNumberFromCurrencyFormat(nurseSalary)) <= 0) {
                OpenErrorAlert('Enter valid renew nurse salary.')
                return;
            }

            setIsLoading(true);
            const tempPatient = {
                service_id: selectedPatientService.service_id,
                current_service_nurse_id: selectedPatientService.service_nurse_id,
                current_service_completed_days: serviceCompletedDutyDays,
                current_nurse_completed_days: nurseCompletedDutyDays,
                current_extended_duty_days: serviceExtendedDutyDays,
                current_extended_duty_days_fee: GetNumberFromCurrencyFormat(serviceExtendedDutyDaysFee),
                current_nurse_extended_duty_days_salary: GetNumberFromCurrencyFormat(serviceExtDutyNurseSalary),
                current_service_completed_date: GetDateFromDayJSValue(serviceCompletedDate),
                current_remarks: notes.trim(),
                patient_id: selectedPatientService.patient_id,
                new_nurse_id: selectedPatientService.nurse_id,
                new_service_type_id: selectedServiceType.id,
                new_duty_days: dutyDays,
                new_start_date: GetDateFromDayJSValue(renewStartDate),
                new_total_salary: GetNumberFromCurrencyFormat(totalFee),
                new_nurse_salary: GetNumberFromCurrencyFormat(nurseSalary),
                new_remarks: remarks.trim()
            };
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(PatientRenewServiceAPIUrl(homeState.loggedUser.companyCode), {
                method: "POST",
                headers: headers,
                body: JSON.stringify(tempPatient)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Patient service renewed successfully.');
                const patientJson = await response.json();
                setIsLoading(false);
                handleCloseClick(true, patientJson.service_id, patientJson.service_nurse_id);

                return;
            }
            ShowAPIError(response);
            return null;
        }
        catch (error) {
            OpenErrorAlert(error)
            return null;
        }
        finally {
            setIsLoading(false);
        }
    };

    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>
                <BootstrapDialog fullWidth maxWidth="md"
                    onClose={handleClose}
                    open={props.isOpen}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                        Renew Patient Service
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Stack>
                            <Box sx={{ paddingBottom: "20px" }}>
                                <Stack>
                                    <Typography gutterBottom variant="subtitle2">
                                        Current Service Details
                                    </Typography>
                                    <Divider />
                                </Stack>
                            </Box>

                            <Box>
                                <Grid container spacing={2}>
                                    <Grid size={4}>
                                        <DatePicker format="DD/MM/YYYY"
                                            label="Completed Date" maxDate={dayjs()}
                                            minDate={dayjs().add(-3, 'day')}
                                            value={serviceCompletedDate}
                                            onChange={(newValue) => { setServiceCompletedDate(newValue); }}
                                            slotProps={{
                                                textField: {
                                                    size: "small", fullWidth: "true", required: "true",
                                                    error: !serviceCompletedDate ? true : false,
                                                    helperText: !serviceCompletedDate ? 'Enter completed date' : ''
                                                },
                                                field: {
                                                    clearable: true,
                                                    onClear: () => setCleared(true)
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth type='number'
                                            label="Completed Duty Days"
                                            value={serviceCompletedDutyDays}
                                            slotProps={{
                                                input: {
                                                    readOnly: true
                                                },
                                            }}
                                            error={serviceCompletedDutyDays <= 0 ? true : false}
                                            helperText={serviceCompletedDutyDays <= 0 ? 'Invalid completed duty days' : ''}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <TextField size="small" fullWidth type='number'
                                            label="Extended Duty Days"
                                            value={serviceExtendedDutyDays}
                                            slotProps={{
                                                input: {
                                                    readOnly: true
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField size="small" fullWidth
                                            label="Extended Duty Days Fee"
                                            value={serviceExtendedDutyDaysFee}
                                            error={serviceExtendedDutyDaysFee < 0 ? true : false}
                                            helperText={serviceExtendedDutyDaysFee < 0 ? 'Invalid fee amount' : ''}
                                            onChange={(event) => {
                                                setServiceExtendedDutyDaysFee(event.target.value);
                                            }}
                                            onBlur={(event) => {
                                                const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                setServiceExtendedDutyDaysFee(tempFormValue);
                                            }}
                                            onFocus={(event) => {
                                                const tempValue = parseFloat(event.target.value ?? 0);
                                                if (tempValue > 0) {
                                                    setServiceExtendedDutyDaysFee(GetNumberFromCurrencyFormat(event.target.value));
                                                } else {
                                                    setServiceExtendedDutyDaysFee('');
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField size="small" fullWidth
                                            label="Extended Duty Nurse Saary"
                                            value={serviceExtDutyNurseSalary}
                                            error={serviceExtDutyNurseSalary < 0 ? true : false}
                                            helperText={serviceExtDutyNurseSalary < 0 ? 'Invalid salary amount' : ''}
                                            onChange={(event) => {
                                                setServiceExtDutyNurseSalary(event.target.value);
                                            }}
                                            onBlur={(event) => {
                                                const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                setServiceExtDutyNurseSalary(tempFormValue);
                                            }}
                                            onFocus={(event) => {
                                                const tempValue = parseFloat(event.target.value ?? 0);
                                                if (tempValue > 0) {
                                                    setServiceExtDutyNurseSalary(GetNumberFromCurrencyFormat(event.target.value));
                                                } else {
                                                    setServiceExtDutyNurseSalary('');
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField size="small" fullWidth
                                            label="Notes"
                                            helperText=""
                                            multiline
                                            rows={4}
                                            value={notes}
                                            onChange={(event) => {
                                                setNotes(event.target.value);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ paddingBottom: "20px", paddingTop: "20px" }}>
                                <Stack>
                                    <Typography gutterBottom variant="subtitle2">
                                        Renew Service Details
                                    </Typography>
                                    <Divider />
                                </Stack>
                            </Box>

                            <Box>
                                <Grid container spacing={2}>
                                    <Grid size={4}>
                                        <Autocomplete
                                            id="combo-box-service-type"
                                            options={serviceTypes} value={selectedServiceType}
                                            getOptionLabel={(option) => option.service_type_name}
                                            onChange={(event, newValue) => {
                                                setSelectedServiceType(newValue);
                                            }}
                                            renderInput={(params) =>
                                                <TextField {...params} label="Service Type" size="small" fullWidth required
                                                    error={selectedServiceType ? true : false}
                                                    helperText={selectedServiceType ? 'Select service type' : ''}
                                                />}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <DatePicker format="DD/MM/YYYY"
                                            label="Start Date" maxDate={dayjs().add(15, 'day')}
                                            minDate={dayjs().add(-3, 'day')}
                                            value={renewStartDate}
                                            onChange={(newValue) => { setRenewStartDate(newValue); }}
                                            slotProps={{
                                                textField: {
                                                    size: "small", fullWidth: "true", required: "true",
                                                    error: !renewStartDate ? true : false,
                                                    helperText: !renewStartDate ? 'Enter start date' : ''
                                                },
                                                field: {
                                                    clearable: true,
                                                    onClear: () => setCleared(true)
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={2}>
                                        <TextField size="small" fullWidth type='number'
                                            label="Duty Days"
                                            value={dutyDays}
                                            error={dutyDays <= 0 ? true : false}
                                            helperText={dutyDays <= 0 ? 'Invalid duty days' : ''}
                                            onChange={(event) => {
                                                setDutyDays(event.target.value ?? 0);
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <TextField size="small" fullWidth
                                            label="End Date"
                                            value={endDate}
                                            slotProps={{
                                                input: {
                                                    readOnly: true
                                                },
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField size="small" fullWidth
                                            label="Total Fee"
                                            value={totalFee}
                                            error={totalFee < 0 ? true : false}
                                            helperText={totalFee < 0 ? 'Invalid fee amount' : ''}
                                            onChange={(event) => {
                                                setTotalFee(event.target.value);
                                            }}
                                            onBlur={(event) => {
                                                const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                setTotalFee(tempFormValue);
                                            }}
                                            onFocus={(event) => {
                                                const tempValue = parseFloat(event.target.value ?? 0);
                                                if (tempValue > 0) {
                                                    setTotalFee(GetNumberFromCurrencyFormat(event.target.value));
                                                } else {
                                                    setTotalFee('');
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <TextField size="small" fullWidth
                                            label="Nurse Salary"
                                            value={nurseSalary}
                                            error={nurseSalary < 0 ? true : false}
                                            helperText={nurseSalary < 0 ? 'Invalid salary amount' : ''}
                                            onChange={(event) => {
                                                setNurseSalary(event.target.value);
                                            }}
                                            onBlur={(event) => {
                                                const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                setNurseSalary(tempFormValue);
                                            }}
                                            onFocus={(event) => {
                                                const tempValue = parseFloat(event.target.value ?? 0);
                                                if (tempValue > 0) {
                                                    setNurseSalary(GetNumberFromCurrencyFormat(event.target.value));
                                                } else {
                                                    setNurseSalary('');
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField size="small" fullWidth
                                            label="Notes"
                                            helperText=""
                                            multiline
                                            rows={4}
                                            value={remarks}
                                            onChange={(event) => {
                                                setRemarks(event.target.value);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {
                            isLoading && <CircularProgress size="30px" />
                        }
                        <Button variant="contained" disabled={isLoading} onClick={handleSaveClick}>
                            Save
                        </Button>
                    </DialogActions>
                </BootstrapDialog> </LocalizationProvider>
        </React.Fragment>

    );
}

export default RenewServiceForm;